import React from "react";
import { useTheme } from "../context/ThemeContext";

/**
 * Cinematic cosmic backdrop for the landing-page hero AND browse-astrologers
 * page (US visitors only, Cosmic Night theme active).
 *
 * Uses the real NASA-Dana-Berry solar-system render the user provided as
 * reference (uploaded to the customer-assets CDN). The image already contains
 * the brilliant sun-burst, Saturn-with-rings, blue foreground planet, dust
 * cloud and asteroid belt — i.e. exactly the look we want. We layer on:
 *   • Slow Ken-Burns zoom + drift (40s loop, alternating) so the still image
 *     feels alive
 *   • A dimming gradient overlay so hero text stays legible
 *   • A subtle CSS star-twinkle layer for extra liveliness
 *
 * Mounts only when `isCosmicNight === true`. The same component is reused
 * across multiple pages — pages just import & render it inside their hero
 * section wrapped by `position: relative; overflow: hidden;`.
 */
const NASA_BACKDROP_URL =
  "https://customer-assets.emergentagent.com/job_astro-connect-42/artifacts/d813otnd_https___blogs-images.forbes.com_startswithabang_files_2019_03_NASA-Dana-Berry-e1711556875766.webp";

export default function CosmicNightHero({ intensity = "full" }) {
  const { isCosmicNight } = useTheme();
  if (!isCosmicNight) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="cosmic-hero"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {/* Base cosmic colour fill underneath the image — guarantees no white
          flash while the image is still loading. */}
      <div className="absolute inset-0" style={{ backgroundColor: "#050818" }} />

      {/* The NASA scene itself. Sized with cover, slow Ken-Burns animation
          so the still image breathes. */}
      <div
        className="absolute inset-0 cosmic-hero-image"
        style={{
          backgroundImage: `url(${NASA_BACKDROP_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Extra twinkling stars layer for liveliness (sits over the image
          but below the text). */}
      <div
        className="absolute inset-0 cosmic-hero-twinkle"
        style={{
          backgroundImage:
            "radial-gradient(1px 1px at 40px 60px,   #ffffff 50%, transparent 60%)," +
            "radial-gradient(1.2px 1.2px at 110px 30px, #c0d6ff 50%, transparent 60%)," +
            "radial-gradient(1px 1px at 200px 90px,  #ffffff 50%, transparent 60%)," +
            "radial-gradient(1.5px 1.5px at 290px 140px, #ffffff 50%, transparent 60%)," +
            "radial-gradient(1px 1px at 360px 50px,  #c0d6ff 50%, transparent 60%)," +
            "radial-gradient(1.5px 1.5px at 50px 170px, #ffffff 50%, transparent 60%)",
          backgroundRepeat: "repeat",
          backgroundSize: "420px 220px",
          opacity: 0.55,
        }}
      />

      {/* Dimming gradient overlay — keeps hero text readable on top of the
          bright sun area. Heavier on the left where hero copy lives, lighter
          on the right so the sun-burst still pops. On `intensity="light"`
          (used on browse-astrologers) the overlay is weaker — we want the
          full scene visible on that page since there's no giant headline. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            intensity === "full"
              ? "linear-gradient(90deg, rgba(5,8,24,0.78) 0%, rgba(5,8,24,0.55) 35%, rgba(5,8,24,0.18) 60%, rgba(5,8,24,0) 100%)"
              : "linear-gradient(180deg, rgba(5,8,24,0.55) 0%, rgba(5,8,24,0.30) 60%, rgba(5,8,24,0.65) 100%)",
        }}
      />

      {/* A second vignette to anchor the eye toward the centre, regardless
          of overlay intensity. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 90% 70% at 50% 50%, transparent 55%, rgba(5,8,24,0.55) 100%)",
        }}
      />
    </div>
  );
}
